/* The full-screen clip player — canon `docs/design/analiza-wideo-2026-09-23/`, boards FV4-06 to
 * FV4-11. Three columns under a 60px bar: the recordings, the stage with its scrubber and controls,
 * and the set with its discussion. Theatre mode folds both side columns away.
 *
 * On the dialog layer (30), not above it: the delete confirmation and the menus it raises are
 * portalled after it and have to paint over it. */

.backdrop {
  position: fixed;
  inset: var(--space-0);
  z-index: 30;
  background: var(--bg);
}

.popup {
  position: fixed;
  inset: var(--space-0);
  z-index: 31;
  display: flex;
  flex-direction: column;
  color: var(--text);
  background: var(--bg);
  outline: none;
  font-variant-numeric: tabular-nums;
}

.bar {
  --sz-avatar: var(--sz-player-client);

  display: flex;
  flex: none;
  gap: var(--space-14px);
  align-items: center;
  height: var(--h-player-bar);
  padding: var(--space-0) var(--space-5);
  border-bottom: var(--border-hairline) solid var(--border);
}

.back {
  gap: var(--space-2);
  height: var(--h-control-icon);
  padding: var(--space-0) var(--space-13px);
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  color: var(--text);
  background: transparent;
  border-radius: var(--radius-sm);
}

.titles {
  display: flex;
  flex-direction: column;
  gap: var(--space-1px);
  min-width: 0;
}

.exercise {
  min-height: 1lh;
  margin: var(--space-0);
  font-size: var(--fs-session);
  font-weight: var(--fw-bold);
  line-height: var(--lh-normal);
  letter-spacing: var(--ls-heading);
}

.client {
  min-height: 1lh;
  font-size: var(--fs-note);
  color: var(--text-2);
}

.spacer {
  flex: 1;
}

.position {
  font-size: var(--fs-label-md);
  color: var(--text-2);
}

.ghost {
  gap: var(--space-2);
  height: var(--h-control);
  padding: var(--space-0) var(--space-10px);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-medium);
  color: var(--text);
  background: transparent;
  border-radius: var(--radius-sm);
}

.icon {
  display: inline-grid;
  flex: none;
  place-items: center;
  width: var(--h-control-field);
  height: var(--h-control-field);
  padding: var(--space-0);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--radius-sm);
}

.icon:hover,
.iconOn {
  color: var(--text);
  background: var(--bg-chip);
}

.glyph {
  width: var(--icon-sm);
  height: var(--icon-sm);
}

.glyphSmall {
  width: var(--icon-xs);
  height: var(--icon-xs);
}

/* The player's keycaps are quieter than the words beside them. */
.kbdInk {
  color: var(--text-2);
}

.body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.list {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-2px);
  width: var(--w-player-list);
  padding: var(--space-3) var(--space-10px);
  overflow: auto;
  border-right: var(--border-hairline) solid var(--border);
}

.eyebrow {
  margin: var(--space-0);
  padding: var(--space-1) var(--space-6px) var(--space-2);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

/* Holds its one line while the count is still unknown. */
.listEyebrow {
  min-height: calc(1lh + var(--space-1) + var(--space-2));
}

.listRows {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
}

.listRowWait {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
  padding: var(--space-7px) var(--space-2);
}

.listRow {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
  width: 100%;
  padding: var(--space-7px) var(--space-2);
  font: inherit;
  color: var(--text);
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--radius-sm);
}

.listRow:hover {
  background: var(--bg-inset);
}

.listRowOn,
.listRowOn:hover {
  background: var(--bg-chip);
}

.thumb {
  display: block;
  flex: none;
  width: var(--w-player-thumb);
  height: var(--h-player-thumb);
  overflow: hidden;
  background: var(--bg-inset);
  border-radius: var(--radius-8px);
}

.thumbWide {
  width: var(--w-player-thumb-wide);
  height: var(--h-player-thumb-wide);
}

.thumbImg {
  display: block;
  width: 100%;
  height: 100%;
}

.listText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: 0;
}

.listDate {
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-semibold);
}

.listMeta {
  font-size: var(--fs-label);
  color: var(--text-2);
}

.by {
  --sz-avatar: var(--sz-forma-avatar-sm);

  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  font-size: var(--fs-label);
  color: var(--text-2);
}

.byYou {
  color: var(--text);
}

.personComment {
  margin-top: var(--space-2px);
}

.skelPerson {
  width: var(--sz-avatar);
  height: var(--sz-avatar);
}

.dot {
  flex: none;
  width: var(--space-7px);
  height: var(--space-7px);
  background: var(--accent);
  border-radius: var(--r-circle);
}

.stageColumn {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  background: var(--bg-sunken);
}

.stageArea {
  position: relative;
  display: flex;
  flex: 1;
  gap: var(--space-4);
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: var(--space-20px) var(--space-6);
  container-type: size;
}

.stageBox {
  position: relative;
  flex: none;
  width: min(100cqw, calc(100cqh * var(--clip-ratio)));
  height: min(100cqh, calc(100cqw / var(--clip-ratio)));
  overflow: hidden;
  background: var(--player-stage);
  border-radius: var(--radius-12px);
}

.stageBoxHalf {
  width: min(calc((100cqw - var(--space-4)) / 2), calc(100cqh * var(--clip-ratio)));
  height: min(100cqh, calc((100cqw - var(--space-4)) / 2 / var(--clip-ratio)));
}

.video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transform-origin: var(--zoom-origin, 50% 50%);
  transition-duration: var(--dur-slow);
  transition-property: transform;
  transition-timing-function: var(--ease);
}

.videoZoomed {
  cursor: grab;
  transform: scale(var(--scale-player-zoom));
}

/* The compare date and the zoom note stand one under the other, never on top of each other. */
.stageLabels {
  position: absolute;
  top: var(--space-10px);
  left: var(--space-10px);
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  align-items: flex-start;
  pointer-events: none;
}

.stageLabel {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  height: var(--h-player-chip);
  padding: var(--space-0) var(--space-9px);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-on-photo);
  pointer-events: none;
  background: var(--wash-on-photo-strong);
  border-radius: var(--radius-7px);
}

.stageNotice {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  align-items: flex-start;
  max-width: var(--w-dead-end-body);
  font-size: var(--fs-body);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

.filmNotice {
  position: absolute;
  inset: var(--space-0);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
  font-size: var(--fs-body);
  line-height: var(--lh-relaxed);
  color: var(--text-on-photo);
  text-align: center;
  background: var(--wash-on-photo-strong);
}

.filmRetry {
  color: var(--text-on-photo);
  background: var(--wash-on-photo);
  border-color: var(--wash-on-photo);
}

.compareRow {
  display: flex;
  flex: none;
  gap: var(--space-10px);
  align-items: center;
  justify-content: center;
  padding: var(--space-0) var(--space-6) var(--space-6px);
  font-size: var(--fs-label-md);
  color: var(--text-2);
}

.compareOffset {
  min-width: var(--w-player-offset);
  font-weight: var(--fw-semibold);
  color: var(--text);
  text-align: center;
}

.chip {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  height: var(--h-player-chip);
  padding: var(--space-0) var(--space-11px);
  font: inherit;
  font-size: var(--fs-label-md);
  color: var(--text-2);
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-chip);
}

.chipOn {
  color: var(--text);
  border-color: var(--text);
}

.controls {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-1);
  justify-content: flex-end;
  min-height: var(--h-player-controls);
  padding: var(--space-1) var(--space-6) var(--space-14px);
}

.scrub {
  position: relative;
  height: var(--h-player-scrub);
  touch-action: none;
}

.scrubControl {
  position: absolute;
  inset: var(--space-0);
  cursor: pointer;
}

.rail {
  position: absolute;
  top: calc((var(--h-player-scrub) - var(--h-player-rail)) / 2);
  right: 0;
  left: 0;
  height: var(--h-player-rail);
  background: var(--border-2);
  border-radius: var(--radius-hair);
}

.scrub:hover .rail {
  background: var(--border-3);
}

.fill {
  height: 100%;
  background: var(--text);
  border-radius: var(--radius-hair);
}

.loopBand {
  position: absolute;
  top: calc((var(--h-player-scrub) - var(--h-player-band)) / 2);
  height: var(--h-player-band);
  pointer-events: none;
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border-3);
  border-radius: var(--radius-bar);
}

.pin {
  position: absolute;
  top: calc((var(--h-player-scrub) - var(--h-player-pin)) / 2);
  z-index: 1;
  width: var(--w-player-pin);
  height: var(--h-player-pin);
  padding: var(--space-0);
  cursor: pointer;
  background: var(--accent);
  border: 0;
  border-radius: var(--radius-hair);
  transform: translateX(-50%);
}

.head {
  position: absolute;
  top: calc((var(--h-player-scrub) - var(--sz-player-head)) / 2);
  z-index: 2;
  width: var(--sz-player-head);
  height: var(--sz-player-head);
  background: var(--text);
  border-radius: var(--r-circle);
  box-shadow: var(--ring-drop-line);
  transform: translateX(-50%);
}

.head:has(:focus-visible) {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

.hover {
  position: absolute;
  bottom: calc(var(--h-player-scrub) + var(--space-6px));
  z-index: 3;
  width: var(--w-player-hover);
  overflow: hidden;
  pointer-events: none;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-8px);
  transform: translateX(-50%);
}

.hoverHidden {
  visibility: hidden;
}

.hoverVideo {
  display: block;
  width: 100%;
  height: var(--h-player-hover);
  object-fit: cover;
  background: var(--player-stage);
}

.hoverTime {
  padding: var(--space-1) var(--space-0);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  text-align: center;
}

/* Two groups that fold onto two rows where the stage column is too narrow for one. */
.controlRow {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-3);
  align-items: center;
  justify-content: space-between;
}

.controlGroup {
  display: flex;
  gap: var(--space-1);
  align-items: center;
}

.clock {
  min-width: var(--w-player-clock);
  margin-inline-start: var(--space-2);
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-semibold);
}

.frame {
  min-width: var(--w-player-frame);
  font-size: var(--fs-note);
  color: var(--text-2);
}

.loopChip {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  height: var(--h-player-chip);
  padding: var(--space-0) var(--space-11px);
  font-size: var(--fs-label-md);
  color: var(--text);
  white-space: nowrap;
  border: var(--border-hairline) solid var(--text);
  border-radius: var(--r-chip);
}

.rates {
  margin-inline-end: var(--space-2);
}

.skelLine {
  width: 60%;
  height: var(--fs-note);
}

.skelTitle {
  width: 45%;
  height: var(--fs-account-code);
}

.skelChips {
  width: 80%;
  height: var(--h-player-chip);
}

.aside {
  position: relative;
  display: flex;
  flex: none;
  flex-direction: column;
  width: var(--w-player-aside);
  min-height: 0;
  border-left: var(--border-hairline) solid var(--border);
}

.setBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-10px);
  min-height: var(--h-player-set);
  padding: var(--space-5) var(--space-20px) var(--space-4);
  border-bottom: var(--border-hairline) solid var(--border);
}

.setTop {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  justify-content: space-between;
}

.setTitle {
  margin: var(--space-0);
  font-size: var(--fs-account-code);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  letter-spacing: var(--ls-display);
}

.setTitleSmall {
  font-size: var(--fs-stat);
  letter-spacing: var(--ls-title);
}

.setChips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px);
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

.setChip {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  height: var(--h-player-chip);
  padding: var(--space-0) var(--space-10px);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-8px);
}

.setChipFilmed {
  color: var(--text);
  border-color: var(--border-3);
}

.setNote {
  margin: var(--space-0);
  font-size: var(--fs-label-md);
  color: var(--text-2);
}

.expiry {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
  margin: var(--space-0);
  padding: var(--space-10px) var(--space-3);
  font-size: var(--fs-label-md);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
}

.expiryGlyph {
  flex: none;
  width: var(--icon-sm);
  height: var(--icon-sm);
  color: var(--warn-ink);
}

.discussionHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-14px) var(--space-20px) var(--space-6px);
}

.discussionTitle {
  margin: var(--space-0);
  font-size: var(--fs-body);
  font-weight: var(--fw-semibold);
}

.count {
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

.comments {
  flex: 1;
  min-height: 0;
  margin: var(--space-0);
  padding: var(--space-0) var(--space-10px);
  overflow: auto;
  list-style: none;
}

.commentsEmpty {
  margin: var(--space-0);
  padding: var(--space-10px);
  font-size: var(--fs-button-sm);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

.comment {
  --sz-avatar: var(--sz-player-avatar);

  display: flex;
  gap: var(--space-10px);
  padding: var(--space-10px);
  border-radius: var(--radius-sm);
}

.comment:hover {
  background: var(--bg-inset);
}

.commentPending {
  opacity: 0.6;
}

.stamp {
  display: inline-flex;
  flex: none;
  gap: var(--space-1);
  align-items: center;
  justify-content: center;
  min-width: var(--w-player-stamp);
  height: var(--h-player-stamp);
  padding: var(--space-0) var(--space-6px);
  font: inherit;
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--accent-ink);
  cursor: pointer;
  background: var(--accent-soft);
  border: 0;
  border-radius: var(--radius-xs);
}

.stampNone {
  color: var(--text-2);
  cursor: default;
  background: transparent;
}

.commentBody {
  flex: 1;
  min-width: 0;
}

.commentHead {
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
}

.who {
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
}

.when {
  font-size: var(--fs-label);
  color: var(--text-2);
}

.textLink {
  padding: var(--space-0);
  font: inherit;
  font-size: var(--fs-label);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--radius-xs);
}

.textLink:hover {
  color: var(--text);
}

.commentText {
  margin: var(--space-3px) var(--space-0) var(--space-0);
  font-size: var(--fs-body);
  line-height: var(--lh-relaxed);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.edit {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-top: var(--space-6px);
}

.editActions {
  display: flex;
  gap: var(--space-2);
  justify-content: flex-end;
}

.field {
  width: 100%;
  min-height: var(--h-player-composer);
  padding: var(--space-11px) var(--space-3);
  font: inherit;
  font-size: var(--fs-body);
  color: var(--text);
  resize: none;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-12px);
  outline: none;
}

.field:focus {
  border-color: var(--border-3);
}

.composer {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4) var(--space-4);
  border-top: var(--border-hairline) solid var(--border);
}

.pinRow {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  min-height: var(--h-player-chip);
}

.pinHint {
  font-size: var(--fs-note);
  color: var(--text-2);
}

.fieldRow {
  --sz-avatar: var(--sz-player-avatar);

  display: flex;
  gap: var(--space-10px);
}

.composerFoot {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.composerHint {
  flex: 1;
  font-size: var(--fs-label);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

.send {
  gap: var(--space-2);
  height: var(--h-control-field);
  padding: var(--space-0) var(--space-14px);
  font-size: var(--fs-body);
  font-weight: var(--fw-semibold);
  border-radius: var(--radius-sm);
}

.small {
  height: var(--h-control);
  padding: var(--space-0) var(--space-3);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-semibold);
  border-radius: var(--radius-sm);
}

.error {
  margin: var(--space-0);
  font-size: var(--fs-label);
  color: var(--danger-ink);
}

.sheetBackdrop {
  position: fixed;
  inset: var(--space-0);
  z-index: 50;
  background: var(--scrim);
}

.sheet {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 51;
  width: min(var(--w-player-sheet), calc(100vw - var(--space-12)));
  max-height: calc(100dvh - var(--space-12));
  padding: var(--space-6) var(--space-7);
  overflow: auto;
  color: var(--text);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  transform: translate(-50%, -50%);
}

.sheetHead {
  display: flex;
  gap: var(--space-4);
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-10px);
}

.sheetTitle {
  margin: var(--space-0);
  font-size: var(--fs-brand);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-title);
}

.sheetSub {
  margin: var(--space-0);
  font-size: var(--fs-note);
  color: var(--text-2);
}

.sheetGrid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: var(--space-10);
  margin: var(--space-0);
}

.sheetColumn {
  margin: var(--space-0);
}

.sheetRow {
  display: flex;
  gap: var(--space-4);
  align-items: center;
  justify-content: space-between;
  padding: var(--space-5px) var(--space-0);
  font-size: var(--fs-button-sm);
  color: var(--text-2);
}

.sheetRow dd {
  margin: var(--space-0);
  font-weight: var(--fw-semibold);
  color: var(--text);
}

.menuNote {
  max-width: var(--w-forma-tip);
  padding: var(--space-2) var(--space-10px) var(--space-6px);
  font-size: var(--fs-label);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

/* Over the composer, so it never takes a click from it; writing in the composer dismisses it. */
.status {
  position: absolute;
  bottom: var(--space-7);
  left: 50%;
  z-index: 5;
  pointer-events: none;
  padding: var(--space-11px) var(--space-4);
  font-size: var(--fs-body);
  font-weight: var(--fw-semibold);
  color: var(--on-ink);
  background: var(--text);
  border-radius: var(--radius-12px);
  transform: translateX(-50%);
}

.statusIdle {
  visibility: hidden;
}

.back:hover,
.ghost:hover {
  border-color: var(--border-3);
}

.icon:focus-visible,
.listRow:focus-visible,
.stamp:focus-visible,
.pin:focus-visible,
.textLink:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

.unsent {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-3);
  align-items: baseline;
  padding: var(--space-2) var(--space-3);
  font-size: var(--fs-label-md);
  color: var(--text-2);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
}

.unsentText {
  flex: 1 1 100%;
  min-width: 0;
  overflow-wrap: anywhere;
}

.unsentText b {
  font-weight: var(--fw-semibold);
  color: var(--danger-ink);
}

/* Theatre mode: the side columns fold away but stay mounted, so a draft or a scroll survives. */
.folded {
  width: 0;
  padding: var(--space-0);
  overflow: hidden;
  visibility: hidden;
  border: 0;
}
